// Header Stats component - displays mesh stats below header in normal mode
import React, { useState } from 'react';
import type { MeshControls } from '../types';

// Mini tooltip component for header stats
function StatsTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<'center' | 'left' | 'right'>('center');
  
  React.useEffect(() => {
    if (show && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 192; // w-48 = 12rem = 192px
      const viewportWidth = window.innerWidth;
      
      // Check if tooltip would go off the right edge
      if (rect.left + tooltipWidth / 2 > viewportWidth - 10) {
        setPosition('right');
      }
      // Check if tooltip would go off the left edge
      else if (rect.left - tooltipWidth / 2 < 10) {
        setPosition('left');
      } else {
        setPosition('center');
      }
    }
  }, [show]);
  
  const positionClasses = {
    center: 'left-1/2 -translate-x-1/2',
    left: 'left-0',
    right: 'right-0',
  };
  
  const arrowClasses = {
    center: 'left-1/2 -translate-x-1/2',
    left: 'left-4',
    right: 'right-4',
  };
  
  return (
    <div className="relative inline-block ml-1 z-[9999]">
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={(e) => {
          e.stopPropagation();
          setShow(!show);
        }}
        className="text-zinc-500 hover:text-cyan-400 transition-colors"
        aria-label="More information"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      
      {show && (
        <div
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          className={`absolute ${positionClasses[position]} top-full mt-2 w-48 p-2 bg-zinc-800 border border-cyan-500/50 rounded-lg shadow-2xl text-[10px] text-zinc-200 leading-relaxed pointer-events-auto z-[9999]`}
        >
          {text}
          <div className={`absolute -top-1 ${arrowClasses[position]} w-2 h-2 bg-zinc-800 border-l border-t border-cyan-500/50 transform rotate-45`}></div>
        </div>
      )}
    </div>
  );
}

interface HeaderStatsProps {
  controls: MeshControls;
  mesh: { nodes: any[]; edges: any[] };
  activePackets: number;
}

export function HeaderStats({ controls, mesh, activePackets }: HeaderStatsProps) {
  return (
    <div className="border-t border-zinc-800/50 bg-zinc-950/80 backdrop-blur-sm relative z-10 overflow-visible">
      <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8 py-2 md:py-3 overflow-visible">
        <div className="flex items-center justify-between flex-wrap gap-2 md:gap-4 overflow-visible">
          {/* Mesh Stats */}
          <div className="flex items-center gap-3 md:gap-6 text-[10px] md:text-xs">
            <div className="flex items-center gap-1 md:gap-2">
              <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-cyan-400 hidden sm:block" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="8" />
              </svg>
              <span className="text-zinc-500 flex items-center">
                <span className="hidden sm:inline">Nodes:</span>
                <span className="sm:hidden">N:</span>
                <StatsTooltip text="Total simulated devices in this visualization. Real-world mesh networks can have varying numbers of nodes." />
              </span>
              <span className="text-cyan-400 font-mono font-medium">{mesh.nodes.length}</span>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2">
              <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-cyan-400 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-zinc-500 flex items-center">
                <span className="hidden sm:inline">Connections:</span>
                <span className="sm:hidden">C:</span>
                <StatsTooltip text="Total simulated radio links between nodes in this visualization (bidirectional). Real mesh connections depend on terrain and device placement." />
              </span>
              <span className="text-cyan-400 font-mono font-medium">{mesh.edges.length}</span>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2">
              <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-yellow-400 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-zinc-500 flex items-center">
                <span className="hidden sm:inline">Active Packets:</span>
                <span className="sm:hidden">P:</span>
                <StatsTooltip text="Total simulated messages currently traveling through this visualization (not per node). This demonstrates packet flow, not actual Meshtastic traffic volume." />
              </span>
              <span className="text-yellow-400 font-mono font-medium">{activePackets}</span>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2 hidden sm:flex">
              <span className="text-zinc-500 flex items-center">
                Mode:
                <StatsTooltip text={controls.topologyMode === 'radius' ? 'Radius: Simulates realistic range-based connections like actual LoRa radio. For educational visualization only.' : 'KNN: Simplified k-nearest neighbors model for clearer educational visualization.'} />
              </span>
              <span className={`font-mono font-medium ${controls.topologyMode === 'radius' ? 'text-emerald-400' : 'text-blue-400'}`}>
                {controls.topologyMode.toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Connection Range (only in radius mode) */}
          {controls.topologyMode === 'radius' && (
            <div className="flex items-center gap-2 md:gap-3">
              <span className="text-[10px] md:text-xs text-zinc-500 flex items-center">
                <span className="hidden sm:inline">Sim. Range:</span>
                <span className="sm:hidden">R:</span>
                <StatsTooltip text="Simulated maximum connection distance for this visualization. Real Meshtastic range varies widely based on terrain, obstacles, antenna, and device settings." />
              </span>
              <div className="relative hidden sm:block">
                <div 
                  className="h-1 bg-gradient-to-r from-cyan-500/30 to-cyan-500 rounded-full"
                  style={{ 
                    width: `${controls.maxRange * 150}px`,
                    maxWidth: '80px'
                  }}
                />
                <div className="absolute -right-0.5 -top-0.5 w-2 h-2 bg-cyan-400 rounded-full border border-zinc-900" />
              </div>
              <span className="text-[10px] md:text-xs text-cyan-400 font-mono">
                {controls.distanceUnit === 'mi' 
                  ? `${(controls.maxRange * 50).toFixed(1)} mi`
                  : `${(controls.maxRange * 80).toFixed(1)} km`
                }
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

